/** PRD-006 schedule short-circuits — calls the agent didn't answer because the practice was closed.
 *
 *  Two ways the FE detects a closure on a call row:
 *    1. Future-compatible: `closeReason` is set to `"vacation"` / `"out_of_hours"` on a `call.ended` row.
 *       This is the path PRD-008 envisaged but the agent hasn't switched to yet.
 *    2. Current reality: the agent emits `call.failed` with `reason="closed:vacation"` etc., the
 *       lifecycle handler stores it in `call.failure_reason`. The FE infers the closure kind from the
 *       `closed:` prefix.
 *
 *  Both paths funnel through `inferClosureKind` so the rest of the UI only deals with one normalised
 *  string. When the agent finally switches to `call.ended`, this helper keeps working unchanged.
 */
export type ClosureKind = "vacation" | "out_of_hours" | "info_message";

export const CLOSURE_LABEL: Record<ClosureKind, string> = {
  vacation:     "Dovolená / uzavírka",
  out_of_hours: "Mimo ordinační dobu",
  info_message: "Nahraná informace",
};

export const CLOSURE_BANNER: Record<ClosureKind, string> = {
  vacation:
    "Hovor přijal Hlas Emmy v době dovolené nebo mimořádné uzavírky. Volajícímu byla přehrána uzavírková hláška a hovor byl ukončen — žádné další zpracování neproběhlo.",
  out_of_hours:
    "Hovor přijal Hlas Emmy mimo ordinační dobu. Volajícímu byla přehrána uzavírková hláška a hovor byl ukončen — žádné další zpracování neproběhlo.",
  // Not a closure in the sense the other two are — the assistant answered and the caller got what they rang for.
  // It shares this rendering because the consequences are the same: nothing was taken down and nothing reached Emmy.
  info_message:
    "Volající stiskl osmičku a vyslechl si nahranou informaci ordinace. Hovor tím skončil — požadavek se nezakládá a do Emmy se nic nepředává. Nahrávka je k dispozici níže.",
};

const KNOWN_KINDS = new Set<string>(["vacation", "out_of_hours", "info_message"]);

export const inferClosureKind = (call: {
  closeReason?: string;
  failureReason?: string;
}): ClosureKind | undefined => {
  if (call.closeReason && KNOWN_KINDS.has(call.closeReason)) return call.closeReason as ClosureKind;
  const fr = call.failureReason;
  if (fr && fr.startsWith("closed:")) {
    const kind = fr.slice("closed:".length);
    if (KNOWN_KINDS.has(kind)) return kind as ClosureKind;
  }
  return undefined;
};
